import { FormEventHandler } from "react"
import { useForm } from "@inertiajs/react"
import { AppLayout } from "../../components/AppLayout"
import { Field } from "../../components/Field"
import { TextInput } from "../../components/TextInput"
import { TextArea } from "../../components/TextArea"

type Form = {
  title: string
  slug: string
  phone: string
  whatsapp: string
  description: string
  category: string
  create_user: boolean
  user_username: string
  user_password: string
  user_password_confirmation: string
  whatsapp_message: string
}

type Props = {
  allow_user_creation: boolean
  create_card_path: string
  customer_mode?: boolean
  category_options: Record<string, string>
}

export default function NewCard({ allow_user_creation, create_card_path, customer_mode = false, category_options }: Props) {
  const { data, setData, post, processing, errors } = useForm<Form>({
    title: "",
    slug: "",
    phone: "",
    whatsapp: "",
    description: "",
    category: "",
    create_user: false,
    user_username: "",
    user_password: "",
    user_password_confirmation: "",
    whatsapp_message: "",
  })

  const submit: FormEventHandler = (event) => {
    event.preventDefault()
    post(create_card_path)
  }

  return (
    <AppLayout title="יצירת כרטיס חדש" customerMode={customer_mode}>
      <div className="mx-auto max-w-2xl">
        <h1 className="mb-2 text-3xl font-bold">בואו ניצור את הכרטיס שלך</h1>
        <p className="mb-6 text-slate-600">נתחיל בכמה פרטים בסיסיים. אחר כך אפשר יהיה להוסיף תמונות, עיצוב ועוד.</p>

        <form onSubmit={submit} className="glass-card space-y-4 p-6">
          <Field label="שם העסק" error={errors.title}>
            <TextInput value={data.title} onChange={(e) => setData("title", e.target.value)} />
          </Field>
          <Field label="כתובת" error={errors.slug} hint="רק אותיות לטיניות קטנות, מספרים ומקפים">
            <TextInput dir="ltr" value={data.slug} onChange={(e) => setData("slug", e.target.value)} />
          </Field>
          <Field label="טלפון" error={errors.phone}>
            <TextInput dir="ltr" value={data.phone} onChange={(e) => setData("phone", e.target.value)} />
          </Field>
          <Field label="מספר וואטסאפ" error={errors.whatsapp} hint="לינק וואטסאפ ייווצר אוטומטית">
            <TextInput dir="ltr" value={data.whatsapp} onChange={(e) => setData("whatsapp", e.target.value)} />
          </Field>
          <Field label="הודעת וואטסאפ" error={errors.whatsapp_message} hint="אופציונלי, יתווסף לקישור">
            <TextInput value={data.whatsapp_message} onChange={(e) => setData("whatsapp_message", e.target.value)} />
          </Field>
          <Field label="תיאור קצר" error={errors.description}>
            <TextArea value={data.description} onChange={(e) => setData("description", e.target.value)} />
          </Field>
          <Field label="תחום העסק" error={errors.category} hint="יעזור לנו להציג את הכרטיס במקום הנכון">
            <select value={data.category} onChange={(e) => setData("category", e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100">
              <option value="">בחירת תחום (אופציונלי)</option>
              {Object.entries(category_options).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </Field>
          {allow_user_creation ? (
            <div className="space-y-4 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={data.create_user}
                  onChange={(e) => setData("create_user", e.target.checked)}
                />
                <span className="font-medium text-slate-900">יצירת משתמש חדש</span>
              </label>
              {data.create_user ? (
                <div className="space-y-4">
                  <Field label="שם משתמש" error={errors.user_username} hint="האימייל יווצר אוטומטית מהשם משתמש">
                    <TextInput
                      dir="ltr"
                      value={data.user_username}
                      onChange={(e) => setData("user_username", e.target.value)}
                    />
                  </Field>
                  <Field label="סיסמה" error={errors.user_password}>
                    <TextInput
                      type="password"
                      value={data.user_password}
                      onChange={(e) => setData("user_password", e.target.value)}
                    />
                  </Field>
                  <Field label="אימות סיסמה" error={errors.user_password_confirmation}>
                    <TextInput
                      type="password"
                      value={data.user_password_confirmation}
                      onChange={(e) => setData("user_password_confirmation", e.target.value)}
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          ) : null}
          <button disabled={processing} className="btn-primary bg-brand-600">שמירה והמשך לעריכה</button>
        </form>
      </div>
    </AppLayout>
  )
}
